import { useState, useEffect } from 'react';
import { X } from 'lucide-react';

const GA_MEASUREMENT_ID = 'G-P2JSKJGR7S';

// Load Google Analytics
const loadGA = () => {
  if (typeof window === 'undefined') return;
  
  // Check if already loaded
  if (window.gtag) return;
  
  // Load gtag script
  const script = document.createElement('script');
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  script.async = true;
  document.head.appendChild(script);
  
  // Initialize gtag
  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = gtag;
  gtag('js', new Date());
  gtag('config', GA_MEASUREMENT_ID, {
    anonymize_ip: true, // Extra privacy protection
  });
  
  console.log('Google Analytics loaded');
};

// Remove GA cookies
const removeGACookies = () => {
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    const cookieName = cookie.split('=')[0].trim();
    if (cookieName.startsWith('_ga') || cookieName.startsWith('_gid')) {
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${window.location.hostname}`;
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    }
  }
};

export const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  
  useEffect(() => {
    const consent = localStorage.getItem('cookie-consent');
    
    if (consent === 'accepted') {
      loadGA();
      setShowBanner(false);
    } else if (consent === 'declined') {
      setShowBanner(false);
    } else {
      // No choice made yet - show banner after a short delay
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);
  
  const handleAccept = () => {
    localStorage.setItem('cookie-consent', 'accepted');
    loadGA();
    setShowBanner(false);
  };
  
  const handleDecline = () => {
    localStorage.setItem('cookie-consent', 'declined');
    removeGACookies();
    setShowBanner(false);
  };
  
  if (!showBanner) return null;
  
  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6"
      role="dialog"
      aria-labelledby="cookie-title"
      aria-describedby="cookie-description"
    >
      <div className="max-w-4xl mx-auto bg-[#1A1A1A] text-white rounded-lg shadow-2xl overflow-hidden">
        <div className="p-6 md:p-8">
          <div className="flex items-start justify-between gap-4 mb-4">
            <h2 id="cookie-title" className="text-lg md:text-xl font-medium">
              Vi bruger cookies
            </h2>
            <button
              onClick={handleDecline}
              className="text-white/60 hover:text-white transition-colors"
              aria-label="Luk"
            >
              <X size={20} />
            </button>
          </div>
          
          <p id="cookie-description" className="text-white/70 text-sm md:text-base mb-4">
            Vi bruger cookies til at analysere trafik på vores hjemmeside og forbedre din oplevelse. 
            Ingen personlige data deles med tredjeparter til markedsføringsformål.
          </p>
          
          {showDetails && (
            <div className="bg-white/10 rounded-lg p-4 mb-4 text-sm">
              <h3 className="font-medium mb-2">Cookies vi bruger:</h3>
              <ul className="space-y-2 text-white/70">
                <li>
                  <strong className="text-white">Google Analytics (_ga, _gid)</strong>
                  <br />
                  Hjælper os med at forstå, hvordan besøgende bruger vores hjemmeside. 
                  IP-adresser anonymiseres.
                </li>
                <li>
                  <strong className="text-white">Samtykke (cookie-consent)</strong>
                  <br />
                  Husker dit valg om cookies. Denne cookie er nødvendig og sættes altid.
                </li>
              </ul>
            </div>
          )}
          
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="text-white/60 hover:text-white text-sm underline transition-colors"
            >
              {showDetails ? 'Skjul detaljer' : 'Vis detaljer'}
            </button>
            
            <div className="flex gap-3">
              <button
                onClick={handleDecline}
                className="px-6 py-3 border border-white/30 text-white text-sm font-medium rounded hover:bg-white/10 transition-colors"
              >
                Kun nødvendige
              </button>
              <button
                onClick={handleAccept}
                className="px-6 py-3 bg-white text-[#1A1A1A] text-sm font-medium rounded hover:bg-white/90 transition-colors"
              >
                Acceptér alle
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Hook to check consent status
export const useAnalyticsConsent = () => {
  const [hasConsent, setHasConsent] = useState(false);
  
  useEffect(() => {
    setHasConsent(localStorage.getItem('cookie-consent') === 'accepted');
  }, []);
  
  return hasConsent;
};

// Function to reset consent (useful for cookie policy page)
export const resetCookieConsent = () => {
  localStorage.removeItem('cookie-consent');
  removeGACookies();
  window.location.reload();
};

export default CookieConsent;
